import { useEffect, useState, useRef } from "react";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Phone, PhoneOff, Brain, Loader2, Mic, MicOff } from "lucide-react";

const KEYS = [
  ["1",""],["2","ABC"],["3","DEF"],["4","GHI"],["5","JKL"],["6","MNO"],
  ["7","PQRS"],["8","TUV"],["9","WXYZ"],["*",""],["0","+"],["#",""]
];

export default function Dialer() {
  const [number, setNumber] = useState("");
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [activeCall, setActiveCall] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [callTimer, setCallTimer] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [lastScored, setLastScored] = useState(null);
  const [micReady, setMicReady] = useState(false);

  const timerRef = useRef();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/leads"), api.get("/calls")]);
    setLeads(l.data); setCalls(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        setMicReady(true);
      } catch { setMicReady(false); }
    })();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (activeCall) timerRef.current = setInterval(() => setCallTimer((t) => t + 1), 1000);
    else { clearInterval(timerRef.current); setCallTimer(0); }
    return () => clearInterval(timerRef.current);
  }, [activeCall]);

  const press = (k) => setNumber((n) => (n + k).slice(0, 20));
  const back = () => setNumber((n) => n.slice(0, -1));

  const startCall = async () => {
    if (!number) return toast.error("Enter a number first");
    try {
      let stream = streamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream; setMicReady(true);
      }
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.start();
      mediaRecorderRef.current = mr;
      setActiveCall({ phone: number, lead_id: selectedLead?.id || null, started: Date.now() });
      toast.success("Call live · AI auto-scoring enabled");
    } catch {
      toast.error("Mic permission needed for AI scoring");
      setActiveCall({ phone: number, lead_id: selectedLead?.id || null, started: Date.now(), noRecord: true });
    }
  };

  const endCall = async (outcome = "completed") => {
    if (!activeCall) return;
    const duration = callTimer;
    let logged;
    try {
      const { data } = await api.post("/calls", {
        lead_id: activeCall.lead_id, phone: activeCall.phone,
        outcome, duration_seconds: duration, notes: "",
      });
      logged = data;
    } catch { toast.error("Failed to log call"); setActiveCall(null); return; }

    const wasRec = mediaRecorderRef.current && mediaRecorderRef.current.state === "recording";
    setActiveCall(null);

    if (!wasRec || outcome !== "completed" || duration < 3) {
      toast.success(`Call logged (${duration}s)`); await load(); return;
    }

    const mr = mediaRecorderRef.current;
    await new Promise((res) => { mr.onstop = res; mr.stop(); });
    const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    mediaRecorderRef.current = null;

    if (blob.size < 2000) { toast.info("Recording too short"); await load(); return; }

    setAnalyzing(true);
    toast.info("AI analyzing call...");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "call.webm");
      const { data } = await api.post(`/calls/${logged.id}/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastScored(data);
      toast.success(`AI Score: ${data.ai_score}/100`);
    } catch { toast.error("AI analysis failed"); }
    finally { setAnalyzing(false); await load(); }
  };

  const selectLead = (lead) => { setSelectedLead(lead); setNumber(lead.phone || ""); };
  const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;

  return (
    <Shell>
      <div className="p-4 sm:p-8" data-testid="dialer-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-xs text-amber">&gt; AI_COACHED_DIALER</div>
            <h1 className="font-display text-3xl sm:text-5xl leading-none mt-2">Dial.</h1>
          </div>
          <div className={`flex items-center gap-2 text-[10px] font-mono px-3 py-2 border uppercase tracking-widest ${micReady ? "border-gain text-gain" : "border-warn text-warn"}`} data-testid="mic-status">
            {micReady ? <><Mic size={12} /> MIC READY</> : <><MicOff size={12} /> MIC OFF</>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-6">
          {/* Lead list */}
          <div className="lg:col-span-4 order-2 lg:order-1 border border-term bg-raised">
            <div className="px-4 py-2.5 border-b border-term label-xs">LEADS [{leads.length}]</div>
            <div className="max-h-[300px] lg:max-h-[520px] overflow-auto">
              {leads.length === 0 && <div className="p-5 text-xs text-fg-muted font-mono">No leads. Add some in Pipeline.</div>}
              {leads.map((l) => (
                <button
                  key={l.id} onClick={() => selectLead(l)}
                  data-testid={`dialer-lead-${l.id}`}
                  className={`tap-card w-full text-left px-4 py-3 block ${selectedLead?.id === l.id ? "is-selected" : ""}`}
                >
                  <div className="font-semibold text-sm text-fg">{l.name}</div>
                  <div className="text-[10px] text-fg-muted font-mono">{l.phone || "NO PHONE"}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Dialpad */}
          <div className="lg:col-span-4 order-1 lg:order-2 border border-term bg-raised flex flex-col">
            <div className="p-5 border-b border-term">
              <div className="label-xs">NUMBER</div>
              <div className="font-display text-3xl font-mono mt-1 tracking-wider h-10 text-amber" data-testid="dialer-number-display">{number || "—"}</div>
              {selectedLead && <div className="text-[10px] text-gain font-mono mt-1 uppercase tracking-widest">→ {selectedLead.name}</div>}
              {activeCall && (
                <div className="mt-2 flex items-center gap-2 text-[10px] font-mono text-live uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 bg-live rounded-full pulse-live"></span>
                  LIVE · {fmt(callTimer)} {!activeCall.noRecord && <span className="text-gain">● REC</span>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-px bg-term">
              {KEYS.map(([k, sub]) => (
                <button key={k} onClick={() => press(k)} data-testid={`dialpad-${k}`} className="bg-raised hover:bg-subtle active:bg-muted py-5 flex flex-col items-center transition-colors">
                  <span className="text-2xl font-bold text-fg">{k}</span>
                  <span className="text-[9px] text-fg-subtle tracking-widest">{sub}</span>
                </button>
              ))}
            </div>

            <div className="p-3 space-y-2">
              <button onClick={back} className="w-full text-[10px] uppercase tracking-widest py-2 border border-term font-bold text-fg-muted hover:border-fg-muted" data-testid="dialer-backspace">← Delete</button>
              {!activeCall ? (
                <button onClick={startCall} data-testid="dialer-call-btn" className="w-full py-4 bg-gain text-black font-black uppercase tracking-[0.2em] hover:opacity-90 transition flex items-center justify-center gap-2 text-xs">
                  <Phone size={14} /> Start Call
                </button>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => endCall("completed")} data-testid="dialer-hangup-btn" className="py-3 bg-live text-white font-black uppercase tracking-widest text-[10px] hover:opacity-90 flex items-center justify-center gap-1"><PhoneOff size={12}/> End</button>
                  <button onClick={() => endCall("voicemail")} className="py-3 bg-transparent border border-term font-bold text-[10px] uppercase text-fg-muted hover:border-fg-muted">VMail</button>
                  <button onClick={() => endCall("no_answer")} className="py-3 bg-transparent border border-term font-bold text-[10px] uppercase text-fg-muted hover:border-fg-muted">No Ans</button>
                </div>
              )}
            </div>
          </div>

          {/* AI Coach + Call log */}
          <div className="lg:col-span-4 order-3 space-y-4">
            <div className="border border-term bg-raised p-5 scanline">
              <div className="label-xs flex items-center gap-2 text-amber"><Brain size={12} /> AI_CALL_COACH</div>
              {analyzing ? (
                <div className="flex items-center gap-3 mt-4 text-sm">
                  <Loader2 className="animate-spin text-amber" size={18} />
                  <div>
                    <div className="font-bold text-fg">Transcribing...</div>
                    <div className="text-[10px] text-fg-muted uppercase tracking-widest">WHISPER → GPT-5.2</div>
                  </div>
                </div>
              ) : lastScored ? (
                <div className="mt-2">
                  <div className="label-xs">LAST SCORE</div>
                  <div className="flex items-baseline gap-2">
                    <div className={`font-display text-6xl ${lastScored.ai_score >= 70 ? "text-gain" : lastScored.ai_score >= 40 ? "text-warn" : "text-live"}`}>{lastScored.ai_score}</div>
                    <div className="text-fg-subtle font-mono">/ 100</div>
                  </div>
                  {lastScored.ai_feedback?.feedback && (
                    <ul className="mt-4 space-y-2 text-xs font-mono">
                      {lastScored.ai_feedback.feedback.map((f, i) => (
                        <li key={i} className="text-fg-muted border-l-2 border-amber pl-3">{f}</li>
                      ))}
                    </ul>
                  )}
                  {lastScored.ai_feedback?.rapport != null && (
                    <div className="grid grid-cols-5 gap-px bg-term border border-term mt-4">
                      {["rapport","discovery","presentation","objection_handling","close"].map((k) => (
                        <div key={k} className="bg-raised p-2 text-center">
                          <div className="text-[8px] uppercase tracking-widest text-fg-subtle">{k.split("_")[0]}</div>
                          <div className="font-display text-lg mt-0.5 text-amber">{lastScored.ai_feedback[k] ?? "—"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-3 text-xs text-fg-muted font-mono">
                  {micReady ? "Start a call — AI auto-scores on hang-up." : "Enable mic to unlock AI auto-scoring."}
                </div>
              )}
            </div>

            <div className="border border-term bg-raised">
              <div className="px-4 py-2.5 border-b border-term label-xs">CALL LOG</div>
              <div className="max-h-64 overflow-auto">
                {calls.length === 0 && <div className="p-4 text-xs text-fg-muted font-mono">No calls yet</div>}
                {calls.map((c) => (
                  <div key={c.id} className="tap-row p-3 text-[11px] font-mono">
                    <div className="flex justify-between">
                      <span className="font-bold text-fg">{c.phone}</span>
                      <span className="text-fg-muted uppercase text-[10px]">{c.outcome}</span>
                    </div>
                    <div className="flex justify-between text-fg-subtle mt-1">
                      <span>{fmt(c.duration_seconds)}</span>
                      {c.ai_score != null ? (
                        <span className={`font-bold ${c.ai_score >= 70 ? "text-gain" : c.ai_score >= 40 ? "text-warn" : "text-live"}`}>AI: {c.ai_score}</span>
                      ) : <span>—</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
